/**
 * developmentalDiagnosticInterviewUtils.js
 *
 * Helpers for the "Diagnostic Developmental History" megablock on the Adult
 * Autism Patient Overview. At launch orgs (e.g. The Owl Centre), History and
 * Semi-Structured Interview are conducted back-to-back in a single appointment,
 * so the overview shows one combined card instead of two separate modules.
 *
 * Progress is sequential: the first half of the bar reflects the first module
 * (History for CI-first orgs, SSI for SSI-first orgs), and the second half
 * reflects the second module. Routing follows the same order so Start/Continue
 * always lands the clinician on whichever part of the appointment is next.
 */

/**
 * @param {object} options
 * @param {number} options.historyProgress - 0–100
 * @param {number} options.ssiProgress - 0–100
 * @param {boolean} [options.isSSIFirst=false] - Whether SSI precedes History
 * @returns {number} Combined progress 0–100
 */
export function computeDevelopmentalDiagnosticInterviewProgress({
  historyProgress,
  ssiProgress,
  isSSIFirst = false,
}) {
  const firstProgress = isSSIFirst ? ssiProgress : historyProgress;
  const secondProgress = isSSIFirst ? historyProgress : ssiProgress;

  if (firstProgress < 100) {
    return Math.round(firstProgress / 2);
  }
  return Math.round(50 + secondProgress / 2);
}

/**
 * Resolves Start/Continue/Review paths for the combined DDI card.
 *
 * @param {object} options
 * @param {string} options.patientId
 * @param {number} options.historyProgress
 * @param {number} options.ssiProgress
 * @param {boolean} [options.isSSIFirst=false]
 * @returns {{ startPath: string, reviewPath: string }}
 */
export function getDevelopmentalDiagnosticInterviewPaths({
  patientId,
  historyProgress,
  ssiProgress,
  isSSIFirst = false,
}) {
  const historyPath = `/patient/${patientId}/adult-autism/history`;
  const ssiStartPath = `/patient/${patientId}/proforma/adult-asd/semi-structured-interview`;
  const ssiReviewPath = `/patient/${patientId}/proforma/adult-asd/semi-structured-interview/review`;

  const firstModule = isSSIFirst ? 'ssi' : 'history';
  const secondModule = isSSIFirst ? 'history' : 'ssi';

  const firstProgress = isSSIFirst ? ssiProgress : historyProgress;
  const secondProgress = isSSIFirst ? historyProgress : ssiProgress;

  if (firstProgress < 100) {
    return firstModule === 'history'
      ? { startPath: historyPath, reviewPath: historyPath }
      : { startPath: ssiStartPath, reviewPath: ssiReviewPath };
  }

  if (secondProgress < 100) {
    return secondModule === 'history'
      ? { startPath: historyPath, reviewPath: historyPath }
      : { startPath: ssiStartPath, reviewPath: ssiReviewPath };
  }

  // Both parts complete — Review opens the SSI review (final step of the appointment)
  return { startPath: ssiReviewPath, reviewPath: ssiReviewPath };
}
